import { init, registerRemotes, loadRemote } from '@module-federation/runtime';
import { devToolsStore, GenericChannel } from '@kb-labs/studio-devtools';
import { useState, Component, useMemo, useEffect, useCallback } from 'react';
import { Typography, Button, Tag, Spin, ConfigProvider } from 'antd';
import { WarningOutlined, BugOutlined, ReloadOutlined, CheckOutlined, CopyOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { jsx, jsxs } from 'react/jsx-runtime';
import { getAntDesignComponents, getAntDesignTokens, PageContextProvider } from '@kb-labs/studio-hooks';
import { EventBusProvider } from '@kb-labs/studio-event-bus';

// src/widget-loader.ts
function resolveEntryUrl(url) {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("/")) {
    try {
      const cfg = window.__KB_STUDIO_CONFIG__;
      const apiBase = cfg?.KB_API_BASE_URL ?? "";
      if (apiBase) {
        return new URL(apiBase).origin + url;
      }
    } catch {
    }
  }
  return url;
}
function getMFChannel() {
  let ch = devToolsStore.getChannel("mf-events");
  if (!ch) {
    ch = new GenericChannel("mf-events", "MF Events", "ApiOutlined");
    devToolsStore.registerChannel(ch);
  }
  return ch;
}
var knownRemotes = /* @__PURE__ */ new Map();
var mfInitialized = false;
function initFederation(plugins) {
  if (mfInitialized) {
    return;
  }
  init({
    name: "studioHost",
    remotes: plugins.map((plugin) => ({
      name: plugin.remoteName,
      entry: resolveEntryUrl(plugin.remoteEntryUrl)
    }))
  });
  mfInitialized = true;
  for (const p of plugins) {
    knownRemotes.set(p.remoteName, p.remoteEntryUrl);
  }
}
function syncRemoteEntry(remoteName, currentEntryUrl) {
  if (knownRemotes.get(remoteName) === currentEntryUrl) {
    return;
  }
  registerRemotes([{ name: remoteName, entry: resolveEntryUrl(currentEntryUrl) }], { force: true });
  knownRemotes.set(remoteName, currentEntryUrl);
}
function resetFederation() {
  knownRemotes.clear();
  mfInitialized = false;
}
var PageLoadError = class extends Error {
  remoteName;
  exposedModule;
  constructor(message, remoteName, exposedModule, cause) {
    super(message, { cause });
    this.name = "PageLoadError";
    this.remoteName = remoteName;
    this.exposedModule = exposedModule;
  }
};
function pushMFEvent(ctx, fields) {
  getMFChannel()?.push({ ...ctx, ...fields });
}
function buildErrorPayload(err) {
  return {
    message: err instanceof Error ? err.message : String(err),
    cause: err instanceof Error && err.cause instanceof Error ? err.cause.message : void 0
  };
}
async function loadPageComponent(remoteName, exposedModule, remoteEntryUrl, retries = 2, retryDelay = 1e3) {
  if (remoteEntryUrl) {
    syncRemoteEntry(remoteName, remoteEntryUrl);
  }
  const modulePath = `${remoteName}/${exposedModule.replace("./", "")}`;
  const ctx = {
    id: `mf-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    remoteName,
    exposedModule,
    startedAt: Date.now()
  };
  pushMFEvent(ctx, { status: "loading", attempt: 1 });
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const module = await loadRemote(modulePath);
      if (!module) {
        throw new Error("Module resolved to null");
      }
      if (module.default === void 0) {
        pushMFEvent(ctx, { status: "warning", durationMs: Date.now() - ctx.startedAt, attempt: attempt + 1, isDefaultUndefined: true });
        return module;
      }
      pushMFEvent(ctx, { status: "success", durationMs: Date.now() - ctx.startedAt, attempt: attempt + 1 });
      return module;
    } catch (err) {
      if (attempt === retries) {
        pushMFEvent(ctx, { status: "error", durationMs: Date.now() - ctx.startedAt, attempt: attempt + 1, error: buildErrorPayload(err) });
        throw new PageLoadError(
          `Failed to load page after ${retries + 1} attempts: ${modulePath}`,
          remoteName,
          exposedModule,
          err instanceof Error ? err : void 0
        );
      }
      pushMFEvent(ctx, { status: "loading", attempt: attempt + 2 });
      await new Promise((resolve) => {
        setTimeout(resolve, retryDelay * (attempt + 1));
      });
    }
  }
  throw new PageLoadError("Unreachable", remoteName, exposedModule);
}
var { Text, Paragraph } = Typography;
function PluginErrorUI({
  type,
  pluginId,
  pageId,
  error,
  remoteName,
  entry,
  onRetry
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const isLoad = type === "load";
  const title = isLoad ? "Plugin unavailable" : "Plugin crashed";
  const subtitle = isLoad ? "Could not load the plugin bundle. The service may be down or the plugin is not built." : "An unexpected error occurred while rendering this plugin.";
  const handleCopy = () => {
    const lines = [
      `Plugin: ${pluginId}`,
      `Page: ${pageId}`,
      remoteName ? `Remote: ${remoteName}` : null,
      entry ? `Entry: ${entry}` : null,
      ``,
      `${error.name}: ${error.message}`,
      error.stack ? `
${error.stack}` : null
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    });
  };
  return /* @__PURE__ */ jsx("div", { style: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 320,
    padding: "32px 24px"
  }, children: /* @__PURE__ */ jsxs("div", { style: { maxWidth: 560, width: "100%" }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }, children: [
      isLoad ? /* @__PURE__ */ jsx(WarningOutlined, { style: { fontSize: 18, color: "var(--warning)", flexShrink: 0 } }) : /* @__PURE__ */ jsx(BugOutlined, { style: { fontSize: 18, color: "var(--error)", flexShrink: 0 } }),
      /* @__PURE__ */ jsx(Text, { strong: true, style: { fontSize: 16 }, children: title })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 12, paddingLeft: 32 }, children: [
      /* @__PURE__ */ jsx(Text, { type: "secondary", style: { fontSize: 12, fontFamily: "monospace" }, children: pluginId }),
      /* @__PURE__ */ jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: "\u203A" }),
      /* @__PURE__ */ jsx(Text, { type: "secondary", style: { fontSize: 12, fontFamily: "monospace" }, children: pageId })
    ] }),
    /* @__PURE__ */ jsx(Paragraph, { type: "secondary", style: { fontSize: 13, marginBottom: 16, paddingLeft: 32 }, children: subtitle }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 8, paddingLeft: 32, marginBottom: 16 }, children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          size: "small",
          icon: /* @__PURE__ */ jsx(ReloadOutlined, {}),
          onClick: onRetry,
          children: "Retry"
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          size: "small",
          icon: copied ? /* @__PURE__ */ jsx(CheckOutlined, {}) : /* @__PURE__ */ jsx(CopyOutlined, {}),
          onClick: handleCopy,
          children: copied ? "Copied" : "Copy details"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { style: { paddingLeft: 32 }, children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          onClick: () => setDetailsOpen((v) => !v),
          style: {
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--text-tertiary)",
            fontSize: 12,
            marginBottom: detailsOpen ? 12 : 0
          },
          children: [
            detailsOpen ? /* @__PURE__ */ jsx(UpOutlined, { style: { fontSize: 11 } }) : /* @__PURE__ */ jsx(DownOutlined, { style: { fontSize: 11 } }),
            "Developer details"
          ]
        }
      ),
      detailsOpen && /* @__PURE__ */ jsxs("div", { style: {
        border: "1px solid var(--border-primary)",
        borderRadius: 6,
        overflow: "hidden"
      }, children: [
        /* @__PURE__ */ jsxs("div", { style: {
          padding: "10px 12px",
          borderBottom: "1px solid var(--border-primary)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap"
        }, children: [
          /* @__PURE__ */ jsx(Tag, { color: isLoad ? "orange" : "red", style: { margin: 0 }, children: error.name }),
          remoteName && /* @__PURE__ */ jsxs(Text, { type: "secondary", style: { fontSize: 11, fontFamily: "monospace" }, children: [
            "remote: ",
            remoteName
          ] }),
          entry && /* @__PURE__ */ jsxs(Text, { type: "secondary", style: { fontSize: 11, fontFamily: "monospace" }, children: [
            "entry: ",
            entry
          ] })
        ] }),
        /* @__PURE__ */ jsx("div", { style: {
          padding: "10px 12px",
          borderBottom: error.stack ? "1px solid var(--border-primary)" : void 0,
          fontFamily: "monospace",
          fontSize: 12,
          color: "var(--text-primary)",
          wordBreak: "break-word"
        }, children: error.message }),
        error.stack && /* @__PURE__ */ jsx("div", { style: {
          padding: "10px 12px",
          maxHeight: 200,
          overflowY: "auto",
          background: "var(--bg-tertiary)"
        }, children: /* @__PURE__ */ jsx("pre", { style: {
          margin: 0,
          fontFamily: "monospace",
          fontSize: 11,
          lineHeight: 1.6,
          color: "var(--text-secondary)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        }, children: error.stack }) })
      ] })
    ] })
  ] }) });
}
var PageErrorBoundary = class extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error(
      `[PageErrorBoundary] Plugin "${this.props.pluginId}" page "${this.props.pageId}" crashed:`,
      error,
      errorInfo
    );
    this.props.onError?.(error, errorInfo);
  }
  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };
  render() {
    if (this.state.hasError && this.state.error) {
      return /* @__PURE__ */ jsx(
        PluginErrorUI,
        {
          type: "crash",
          pluginId: this.props.pluginId,
          pageId: this.props.pageId,
          error: this.state.error,
          onRetry: this.handleRetry
        }
      );
    }
    return this.props.children;
  }
};
function PageContainer({
  remoteName,
  entry,
  remoteEntryUrl,
  pageId,
  pluginId,
  config = {},
  permissions = []
}) {
  const [PageComponent, setPageComponent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const themeConfig = useMemo(() => ({
    token: getAntDesignTokens(),
    components: getAntDesignComponents()
  }), []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setPageComponent(null);
    loadPageComponent(remoteName, entry, remoteEntryUrl).then((mod) => {
      if (!cancelled) {
        setPageComponent(() => mod.default);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setLoadError(err);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [remoteName, entry, retryKey]);
  const handleRetry = useCallback(() => {
    setRetryKey((k) => k + 1);
  }, []);
  if (loading) {
    return /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }, children: /* @__PURE__ */ jsx(Spin, { size: "large" }) });
  }
  if (loadError) {
    const mfError = loadError;
    return /* @__PURE__ */ jsx(
      PluginErrorUI,
      {
        type: "load",
        pluginId,
        pageId,
        error: loadError,
        remoteName: mfError.remoteName ?? remoteName,
        entry: mfError.exposedModule ?? entry,
        onRetry: handleRetry
      }
    );
  }
  if (!PageComponent) {
    return null;
  }
  const pageContext = { pageId, pluginId, config, permissions };
  return /* @__PURE__ */ jsx(PageErrorBoundary, { pageId, pluginId, children: /* @__PURE__ */ jsx(ConfigProvider, { theme: themeConfig, children: /* @__PURE__ */ jsx(EventBusProvider, { children: /* @__PURE__ */ jsx(PageContextProvider, { value: pageContext, children: /* @__PURE__ */ jsx(PageComponent, {}) }) }) }) }, retryKey);
}

export { PageContainer, PageErrorBoundary, PageLoadError, PluginErrorUI, initFederation, loadPageComponent, resetFederation, syncRemoteEntry };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map